import { useState } from "react";
import { Reveal } from "./Reveal";
import { AREAS, EMAIL, PHONE, PHONE_HREF, WA } from "@/lib/site";

const SERVICE_OPTIONS = ["Interior", "Exterior", "Commercial", "Custom"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "Interior", desc: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const parts = [
      form.name && `Name: ${form.name}`,
      form.phone && `Phone: ${form.phone}`,
      form.email && `Email: ${form.email}`,
      `Service: ${form.service}`,
      form.desc && `Project: ${form.desc}`,
    ].filter(Boolean);
    window.open(WA(parts.join(" | ")), "_blank");
  };

  return (
    <section id="contact" className="bg-graybg py-20 md:py-28">
      <div className="mx-auto grid max-w-[1200px] items-start gap-14 px-5 md:px-8 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <h2 className="font-display text-4xl font-light leading-[1.12] text-navy md:text-5xl">
            Ready to transform <em className="italic">your property?</em>
          </h2>
          <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-[#5c6b7a] md:text-base">
            Schedule a free, no-obligation estimate today. We respond within one business day with
            a transparent line-item quote.
          </p>

          <div className="mt-10 space-y-6">
            <a data-testid="contact-call" href={PHONE_HREF} className="flex items-center gap-4 group">
              <span className="icon-box group-hover:border-amber">
                <svg viewBox="0 0 24 24" className="h-4.5 w-4.5" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.8a2 2 0 0 1-.4 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.9.6 2.8.7a2 2 0 0 1 1.7 2z" />
                </svg>
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.22em] text-[#8a97a5]">Call</span>
                <span className="block text-lg font-semibold text-navy">{PHONE}</span>
              </span>
            </a>

            <a data-testid="contact-email" href={`mailto:${EMAIL}`} className="flex items-center gap-4 group">
              <span className="icon-box group-hover:border-amber">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 6L2 7" />
                </svg>
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.22em] text-[#8a97a5]">Email</span>
                <span className="block break-all text-lg font-semibold text-navy">{EMAIL}</span>
              </span>
            </a>

            <div className="flex items-center gap-4">
              <span className="icon-box">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </span>
              <span>
                <span className="block text-[11px] uppercase tracking-[0.22em] text-[#8a97a5]">Service areas</span>
                <span className="block text-lg font-semibold text-navy">{AREAS}</span>
              </span>
            </div>
          </div>

          <a
            data-testid="contact-whatsapp-cta"
            href={WA()}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 flex items-center gap-4 rounded-lg bg-[#22c55e] p-5 text-white transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-white/20">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
                <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm4.6 12.1c-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.6.8-.8 1-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4 0-.5.1-.7l.4-.5c.1-.2.2-.3.3-.5v-.5c-.1-.2-.6-1.4-.8-1.9-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.2.3-.9.9-.9 2.2s.9 2.5 1.1 2.7c.1.2 1.8 2.8 4.4 3.9.6.3 1.1.4 1.5.6.6.2 1.2.2 1.6.1.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3z" />
              </svg>
            </span>
            <span className="leading-snug">
              <span className="block text-[10px] uppercase tracking-[0.24em] text-white/80">Prefer WhatsApp?</span>
              <span className="block font-semibold">Message us — we typically reply in minutes.</span>
            </span>
            <span className="ml-auto text-xl" aria-hidden="true">→</span>
          </a>
        </Reveal>

        <Reveal delay={0.12}>
          <form onSubmit={submit} className="border border-[#e6e6e1] bg-white p-7 md:p-10">
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8a97a5]">Full name</span>
                <input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Jane Doe" className="input-box" />
              </label>
              <label className="block">
                <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8a97a5]">Phone</span>
                <input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="(407) 555-0123" className="input-box" />
              </label>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8a97a5]">Email</span>
              <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" className="input-box" />
            </label>

            <div className="mt-5">
              <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8a97a5]">Service needed</span>
              <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                {SERVICE_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`contact-service-${opt.toLowerCase()}`}
                    onClick={() => setForm({ ...form, service: opt })}
                    className={`seg-btn ${form.service === opt ? "active" : ""}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            <label className="mt-5 block">
              <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#8a97a5]">Project description</span>
              <textarea
                data-testid="contact-desc-input"
                rows="5"
                value={form.desc}
                onChange={set("desc")}
                placeholder="Tell us about your project — rooms, square footage, timeline, color ideas..."
                className="textarea-box"
              />
            </label>

            <button data-testid="contact-submit" type="submit" className="btn-amber mt-7 w-full sm:w-auto">
              Submit Estimate Request
              <span aria-hidden="true">→</span>
            </button>
            <p className="mt-4 text-xs text-[#8a97a5]">
              By submitting you agree to be contacted about your project. No spam, ever.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
